import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type {
  MachineState,
  MachineStatusPayload,
  OutgoingCommand,
  ConnectionState as ConnectionStateEnum,
  GCodeFile,
  RunState,
  ErrorPayload,
  ErrorEntry,
  UpdateStatus,
  BLEFileInfo,
  ActiveToolInfo,
} from '@/types/machine.types';
import { HOMED_JOINT_INDICES, isCriticalError } from '@/types/machine.types';
import { fetchTools, buildToolCatalog, formatToolNo } from '@/components/ToolsPage/toolData';

// WebSocket ref kept outside store to avoid serialization issues
let _wsRef: WebSocket | null = null;

// Monotonic ID counter for error entries
let _errorIdCounter = 0;

// Cap the durable MDI response console so it can't grow unbounded over a session.
const MDI_CONSOLE_MAX = 200;

// Commands that stop or e-stop the machine. If one is issued while the socket is
// down it must be surfaced loudly, never dropped silently.
const SAFETY_COMMANDS = new Set<OutgoingCommand['type']>([
  'STOP_PROGRAM',
  'PAUSE_PROGRAM',
  'MACHINE_ESTOP',
  'ESTOP_TOGGLE',
]);

// Resolve the active tool from the spindle number + geometry cache. Number 0 is
// an empty spindle (null). A number with no cached geometry (unknown/empty
// pocket) still shows its label so the DRO/badge reflect the machine.
function deriveActiveTool(
  toolNo: number,
  catalog: Record<number, ActiveToolInfo>,
): ActiveToolInfo | null {
  if (toolNo <= 0) return null;
  return catalog[toolNo] ?? { no: formatToolNo(toolNo), type: '', diameter: '' };
}

// Value-equality for the derived active tool so referential churn (which would
// re-render every consumer) only happens when the tool actually changes.
function activeToolEqual(a: ActiveToolInfo | null, b: ActiveToolInfo | null): boolean {
  if (a === null || b === null) return a === b;
  return a.no === b.no && a.type === b.type && a.diameter === b.diameter;
}

export const useMachineStore = create<MachineState>()((set, get) => ({
  // Machine data (safe defaults)
  position: new Array(9).fill(0) as number[],
  jointPositions: new Array(9).fill(0) as number[],
  workPosition: new Array(9).fill(0) as number[],
  homed: new Array(9).fill(0) as number[],
  estop: false,
  enabled: false,
  machineMode: 'UNKNOWN',
  connectionState: 'DISCONNECTED' as ConnectionStateEnum,
  isHomingInProgress: false,

  // G-Code state
  gcodeFile: null,
  runState: 'idle' as RunState,
  programConfirmedRunning: false,
  motionLine: -1,
  programLineCount: 0,

  // Sequential MDI queue
  mdiRunning: false,
  mdiLine: -1,
  mdiTotal: 0,
  mdiBusy: false,
  mdiHistory: [],

  // Override read-back (percent)
  feedOverride: 100,
  spindleOverride: 100,

  // Active programmed F/S read-back (feed units/min, spindle RPM)
  feedRate: 0,
  spindleSpeed: 0,

  // Spindle on/off (machine authority: reflects M3/M5 from any source)
  spindleOn: false,

  // Active work coordinate system (from LinuxCNC g5x)
  workOffset: new Array(9).fill(0) as number[],
  workOffsetName: 'G54',

  // Errors
  errors: [],
  mdiConsole: [],

  // UI preferences (reset to off on each app launch)
  showJogPadDeco: false,
  jogFeedRate: 100,
  jogIncrement: 0.1,
  gcodePreviewWindow: 17, // default virtualization window (VISIBLE_ROWS + OVERSCAN*2)

  // Auto-Update
  updateStatus: 'idle' as UpdateStatus,
  downloadProgress: 0,
  updateVersion: '',
  appVersion: __APP_VERSION__,
  updateError: null,
  hasActiveAlarm: false,

  // Active tool: `toolInSpindle` (machine authority) resolved against
  // `toolCatalog` (tool-table geometry) into `activeTool`, shown across pages.
  toolInSpindle: 0,
  toolTableVersion: '',
  toolCatalog: {},
  activeTool: null,

  // Phone BLE
  phoneBLEConnected: false,
  phoneBLEAddress: null,
  phoneBLEAdvertising: false,
  lastBLEFile: null,

  updateMachineStatus: (payload: MachineStatusPayload) => {
    const current = get();

    const positionChanged = !arraysEqual(current.position, payload.position);
    const jointPositionsChanged = !arraysEqual(current.jointPositions, payload.jointPositions);
    const workPositionChanged = !arraysEqual(current.workPosition, payload.workPosition);
    const homedChanged = !arraysEqual(current.homed, payload.homed);
    const estopChanged = current.estop !== (payload.estop === 1);
    const enabledChanged = current.enabled !== payload.enabled;
    const modeChanged = current.machineMode !== payload.mode;
    const motionLineChanged = current.motionLine !== payload.motionLine;
    const lineCountChanged = current.programLineCount !== payload.programLineCount;
    const mdiRunningChanged = current.mdiRunning !== payload.mdiRunning;
    const mdiLineChanged = current.mdiLine !== payload.mdiLine;
    const mdiTotalChanged = current.mdiTotal !== payload.mdiTotal;
    const mdiBusyChanged = current.mdiBusy !== payload.mdiBusy;
    const feedOverrideChanged = current.feedOverride !== payload.feedOverride;
    const spindleOverrideChanged = current.spindleOverride !== payload.spindleOverride;
    const feedRateChanged = current.feedRate !== payload.feedRate;
    const spindleSpeedChanged = current.spindleSpeed !== payload.spindleSpeed;
    const spindleOnChanged = current.spindleOn !== payload.spindleOn;
    const workOffsetChanged = !arraysEqual(current.workOffset, payload.workOffset);
    const workOffsetNameChanged = current.workOffsetName !== payload.workOffsetName;
    const toolInSpindleChanged = current.toolInSpindle !== payload.toolInSpindle;
    const toolTableVersionChanged = current.toolTableVersion !== payload.toolTableVersion;

    // Derive runState from backend flags
    const isActive = current.runState !== 'idle';
    let newRunState: RunState | null = null;

    // Latch "the backend confirmed this run is actually executing". After Run,
    // runState is set 'running' optimistically but the first STATUS frames still
    // report programRunning=false while LinuxCNC switches to AUTO and opens/parses
    // the file — a window that grows with program length. Without this latch the
    // 'done' branch below fires immediately, hiding Pause and swapping in a fresh
    // Run button (which restarts the program from line 0 instead of resuming).
    const confirmedRunning = current.programConfirmedRunning || payload.programRunning;
    const confirmChanged = payload.programRunning && !current.programConfirmedRunning;

    if (isActive) {
      if (payload.programPaused && current.runState !== 'paused') {
        newRunState = 'paused';
      } else if (
        payload.programRunning &&
        !payload.programPaused &&
        current.runState !== 'running'
      ) {
        newRunState = 'running';
      } else if (
        confirmedRunning &&
        !payload.programRunning &&
        !payload.programPaused &&
        current.runState !== 'done' &&
        current.runState !== 'idle'
      ) {
        newRunState = 'done';
      }
    }

    if (
      !positionChanged &&
      !jointPositionsChanged &&
      !workPositionChanged &&
      !homedChanged &&
      !estopChanged &&
      !enabledChanged &&
      !modeChanged &&
      !motionLineChanged &&
      !lineCountChanged &&
      !mdiRunningChanged &&
      !mdiLineChanged &&
      !mdiTotalChanged &&
      !mdiBusyChanged &&
      !feedOverrideChanged &&
      !spindleOverrideChanged &&
      !feedRateChanged &&
      !spindleSpeedChanged &&
      !spindleOnChanged &&
      !workOffsetChanged &&
      !workOffsetNameChanged &&
      !toolInSpindleChanged &&
      !toolTableVersionChanged &&
      !confirmChanged &&
      newRunState === null
    ) {
      return;
    }

    const patch: Partial<MachineState> = {};
    if (positionChanged) patch.position = [...payload.position];
    if (jointPositionsChanged) patch.jointPositions = [...payload.jointPositions];
    if (workPositionChanged) patch.workPosition = [...payload.workPosition];
    if (homedChanged) patch.homed = [...payload.homed];
    if (estopChanged) patch.estop = payload.estop === 1;
    if (enabledChanged) patch.enabled = payload.enabled;
    if (modeChanged) patch.machineMode = payload.mode;
    if (motionLineChanged) patch.motionLine = payload.motionLine;
    if (lineCountChanged) patch.programLineCount = payload.programLineCount;
    if (mdiRunningChanged) patch.mdiRunning = payload.mdiRunning;
    if (mdiLineChanged) patch.mdiLine = payload.mdiLine;
    if (mdiTotalChanged) patch.mdiTotal = payload.mdiTotal;
    if (mdiBusyChanged) patch.mdiBusy = payload.mdiBusy;
    if (feedOverrideChanged) patch.feedOverride = payload.feedOverride;
    if (spindleOverrideChanged) patch.spindleOverride = payload.spindleOverride;
    if (feedRateChanged) patch.feedRate = payload.feedRate;
    if (spindleSpeedChanged) patch.spindleSpeed = payload.spindleSpeed;
    if (spindleOnChanged) patch.spindleOn = payload.spindleOn;
    if (workOffsetChanged) patch.workOffset = [...payload.workOffset];
    if (workOffsetNameChanged) patch.workOffsetName = payload.workOffsetName;
    if (newRunState !== null) patch.runState = newRunState;
    if (confirmChanged) patch.programConfirmedRunning = true;
    if (toolInSpindleChanged) {
      patch.toolInSpindle = payload.toolInSpindle;
      // Machine is authority: re-derive the active tool, overriding any
      // optimistic value from a prior Call Tool.
      const nextTool = deriveActiveTool(payload.toolInSpindle, current.toolCatalog);
      if (!activeToolEqual(current.activeTool, nextTool)) patch.activeTool = nextTool;
    }
    if (toolTableVersionChanged) patch.toolTableVersion = payload.toolTableVersion;

    set(patch);
  },

  setConnectionState: (state: ConnectionStateEnum) => {
    if (get().connectionState !== state) {
      set({ connectionState: state });
    }
  },

  setHomingInProgress: (inProgress: boolean) => {
    set({ isHomingInProgress: inProgress });
  },

  setWebSocket: (ws: WebSocket | null) => {
    _wsRef = ws;
  },

  sendCommand: (command: OutgoingCommand) => {
    if (_wsRef && _wsRef.readyState === WebSocket.OPEN) {
      _wsRef.send(JSON.stringify(command));
      return;
    }
    // Socket is down. A safety/critical command must never drop silently: raise
    // a critical alarm so the operator knows the software Stop did not reach the
    // machine and can hit the physical E-Stop.
    if (SAFETY_COMMANDS.has(command.type)) {
      get().addError({
        type: 'ERROR',
        kind: 'NML_ERROR',
        message: `Connection lost — ${command.type} did not reach the machine. Use the physical E-Stop.`,
        timestamp: Date.now(),
      });
    } else {
      console.warn('⚠️ WebSocket not connected — command not sent:', command);
    }
  },

  setGcodeFile: (file: GCodeFile | null) => {
    set({ gcodeFile: file, runState: 'idle', motionLine: -1, programLineCount: 0 });
  },

  setRunState: (state: RunState) => {
    // Starting a fresh run clears the confirmed-running latch so the next
    // startup window is protected again (see updateMachineStatus).
    set(
      state === 'running'
        ? { runState: state, programConfirmedRunning: false }
        : { runState: state },
    );
  },

  addMdiHistory: (line: string) => {
    set({ mdiHistory: [...get().mdiHistory, line] });
  },

  clearMdiHistory: () => {
    set({ mdiHistory: [] });
  },

  /**
   * Add an error from the backend error_channel.
   * Critical errors (NML_ERROR, OPERATOR_ERROR):
   *   - Set hasActiveAlarm = true → blocks UI
   *   - If runState was 'running', force it to 'idle' (state interlock)
   * Info messages (NML_TEXT, OPERATOR_TEXT):
   *   - Append to errors list for toast display
   */
  addError: (payload: ErrorPayload) => {
    const current = get();
    _errorIdCounter++;
    const entry: ErrorEntry = {
      id: `err-${_errorIdCounter.toString()}`,
      kind: payload.kind,
      message: payload.message,
      timestamp: payload.timestamp,
      acknowledged: false,
    };

    // Durable console transcript — appended for every message, capped, and only
    // cleared by the explicit Clear action (independent of the toast lifecycle).
    const nextConsole = [...current.mdiConsole, entry];
    const patch: Partial<MachineState> = {
      errors: [...current.errors, entry],
      mdiConsole:
        nextConsole.length > MDI_CONSOLE_MAX ? nextConsole.slice(-MDI_CONSOLE_MAX) : nextConsole,
    };

    if (isCriticalError(payload.kind)) {
      patch.hasActiveAlarm = true;
      // State interlock: force running → idle on critical error
      if (current.runState === 'running' || current.runState === 'paused') {
        patch.runState = 'idle';
      }
    }

    set(patch);
  },

  /**
   * Acknowledge a critical error. Once all critical errors are acknowledged,
   * hasActiveAlarm is cleared and the UI is unblocked.
   */
  acknowledgeError: (errorId: string) => {
    const current = get();
    const updated = current.errors.map((e) =>
      e.id === errorId ? { ...e, acknowledged: true } : e,
    );

    const stillHasAlarm = updated.some((e) => isCriticalError(e.kind) && !e.acknowledged);

    set({ errors: updated, hasActiveAlarm: stillHasAlarm });
  },

  /** Remove all non-critical (info) messages from the list */
  clearInfoMessages: () => {
    const current = get();
    set({ errors: current.errors.filter((e) => isCriticalError(e.kind)) });
  },

  /** Empty the durable MDI response console (explicit Clear button). */
  clearMdiConsole: () => {
    set({ mdiConsole: [] });
  },

  // UI preference actions
  setShowJogPadDeco: (show: boolean) => set({ showJogPadDeco: show }),
  setGcodePreviewWindow: (count: number) => set({ gcodePreviewWindow: count }),
  setJogFeedRate: (rate: number) => set({ jogFeedRate: rate }),
  setJogIncrement: (increment: number) => set({ jogIncrement: increment }),

  // Auto-Update actions
  setUpdateStatus: (status: UpdateStatus) => set({ updateStatus: status }),
  setDownloadProgress: (percent: number) => set({ downloadProgress: percent }),
  setUpdateVersion: (version: string) => set({ updateVersion: version }),
  setAppVersion: (version: string) => set({ appVersion: version }),
  setUpdateError: (message: string | null) => set({ updateError: message }),

  // Phone BLE actions
  setPhoneBLEConnected: (connected: boolean, address?: string) => {
    set({
      phoneBLEConnected: connected,
      phoneBLEAddress: connected ? (address ?? null) : null,
    });
  },
  setPhoneBLEAdvertising: (active: boolean) => set({ phoneBLEAdvertising: active }),
  setLastBLEFile: (file: BLEFileInfo | null) => set({ lastBLEFile: file }),

  setToolInSpindle: (toolNo: number) => {
    const current = get();
    if (current.toolInSpindle === toolNo) return;
    const nextTool = deriveActiveTool(toolNo, current.toolCatalog);
    const patch: Partial<MachineState> = { toolInSpindle: toolNo };
    if (!activeToolEqual(current.activeTool, nextTool)) patch.activeTool = nextTool;
    set(patch);
  },

  setToolCatalog: (catalog: Record<number, ActiveToolInfo>) => {
    const current = get();
    const nextTool = deriveActiveTool(current.toolInSpindle, catalog);
    const patch: Partial<MachineState> = { toolCatalog: catalog };
    if (!activeToolEqual(current.activeTool, nextTool)) patch.activeTool = nextTool;
    set(patch);
  },

  refreshToolCatalog: async () => {
    try {
      const rows = await fetchTools();
      get().setToolCatalog(buildToolCatalog(rows));
      return true;
    } catch {
      // Backend not up yet (cold boot) or a transient hiccup — keep the
      // last-known catalog and let the caller decide whether to retry.
      return false;
    }
  },
}));

// ==========================================================================
// Granular Selectors
// ==========================================================================

export const usePosition = () => useMachineStore((state) => state.position);
export const useJointPositions = () => useMachineStore((state) => state.jointPositions);
export const useWorkPosition = () => useMachineStore((state) => state.workPosition);
export const useConnectionState = () => useMachineStore((state) => state.connectionState);
export const useEStop = () => useMachineStore((state) => state.estop);
export const useMachineMode = () => useMachineStore((state) => state.machineMode);
export const useEnabled = () => useMachineStore((state) => state.enabled);

export const useHomed = () => useMachineStore((state) => state.homed);

export const useAllHomed = () =>
  useMachineStore((state) => HOMED_JOINT_INDICES.every((i) => state.homed[i] === 1));

export const useIsLocked = () =>
  useMachineStore((state) => {
    const allHomed = HOMED_JOINT_INDICES.every((i) => state.homed[i] === 1);
    return state.estop || !allHomed;
  });

export const useIsHomingInProgress = () => useMachineStore((state) => state.isHomingInProgress);
export const useSendCommand = () => useMachineStore((state) => state.sendCommand);

// G-Code selectors
export const useGcodeFile = () => useMachineStore((state) => state.gcodeFile);
export const useRunState = () => useMachineStore((state) => state.runState);
export const useMotionLine = () => useMachineStore((state) => state.motionLine);
export const useProgramLineCount = () => useMachineStore((state) => state.programLineCount);

// Sequential MDI queue progress (real machine execution)
export const useMdiProgress = () =>
  useMachineStore(
    useShallow((state) => ({
      running: state.mdiRunning,
      line: state.mdiLine,
      total: state.mdiTotal,
    })),
  );

// Single-MDI busy (Call Tool / Get Offset in-flight) — drives the Tool loading modal
export const useMdiBusy = () => useMachineStore((state) => state.mdiBusy);

// Persistent MDI command history (survives tab switches for the app session)
export const useMdiHistory = () => useMachineStore((state) => state.mdiHistory);
export const useAddMdiHistory = () => useMachineStore((state) => state.addMdiHistory);
export const useClearMdiHistory = () => useMachineStore((state) => state.clearMdiHistory);

// Override read-back selectors
export const useFeedOverride = () => useMachineStore((state) => state.feedOverride);
export const useSpindleOverride = () => useMachineStore((state) => state.spindleOverride);

// Active programmed F/S read-back (feed units/min, spindle RPM)
export const useFeedSpindle = () =>
  useMachineStore(
    useShallow((state) => ({ feedRate: state.feedRate, spindleSpeed: state.spindleSpeed })),
  );

// Spindle on/off (machine authority: reflects M3/M5 from any source)
export const useSpindleOn = () => useMachineStore((state) => state.spindleOn);

// Active work coordinate system (name + 9-element offset vector)
export const useWorkOffset = () =>
  useMachineStore(
    useShallow((state) => ({ name: state.workOffsetName, offset: state.workOffset })),
  );

// UI preference selectors
export const useShowJogPadDeco = () => useMachineStore((state) => state.showJogPadDeco);
export const useGcodePreviewWindow = () => useMachineStore((state) => state.gcodePreviewWindow);
export const useJogFeedRate = () => useMachineStore((state) => state.jogFeedRate);
export const useJogIncrement = () => useMachineStore((state) => state.jogIncrement);
export const useSetJogFeedRate = () => useMachineStore((state) => state.setJogFeedRate);
export const useSetJogIncrement = () => useMachineStore((state) => state.setJogIncrement);

// Error selectors
export const useHasActiveAlarm = () => useMachineStore((state) => state.hasActiveAlarm);
export const useErrors = () => useMachineStore((state) => state.errors);
export const useUnacknowledgedAlarms = () =>
  useMachineStore(
    useShallow((state) => state.errors.filter((e) => isCriticalError(e.kind) && !e.acknowledged)),
  );
export const useInfoMessages = () =>
  useMachineStore(useShallow((state) => state.errors.filter((e) => !isCriticalError(e.kind))));

// Durable MDI response console (survives tab switches; cleared only via Clear)
export const useMdiConsole = () => useMachineStore((state) => state.mdiConsole);
export const useClearMdiConsole = () => useMachineStore((state) => state.clearMdiConsole);

// Auto-Update selectors
export const useUpdateStatus = () => useMachineStore((state) => state.updateStatus);
export const useDownloadProgress = () => useMachineStore((state) => state.downloadProgress);
export const useUpdateVersion = () => useMachineStore((state) => state.updateVersion);
export const useAppVersion = () => useMachineStore((state) => state.appVersion);
export const useUpdateError = () => useMachineStore((state) => state.updateError);

// Phone BLE selectors
export const usePhoneBLEConnected = () => useMachineStore((state) => state.phoneBLEConnected);
export const usePhoneBLEAddress = () => useMachineStore((state) => state.phoneBLEAddress);
export const usePhoneBLEAdvertising = () => useMachineStore((state) => state.phoneBLEAdvertising);
export const useLastBLEFile = () => useMachineStore((state) => state.lastBLEFile);

// Active tool: machine-reported spindle tool resolved against the tool catalog,
// shared across the Jog badge, G-code preview and Tool page.
export const useActiveTool = () => useMachineStore((state) => state.activeTool);
export const useToolInSpindle = () => useMachineStore((state) => state.toolInSpindle);
export const useToolTableVersion = () => useMachineStore((state) => state.toolTableVersion);
export const useSetToolInSpindle = () => useMachineStore((state) => state.setToolInSpindle);
export const useSetToolCatalog = () => useMachineStore((state) => state.setToolCatalog);

export const useMachineInfo = () =>
  useMachineStore(
    useShallow((state) => ({
      estop: state.estop,
      enabled: state.enabled,
      machineMode: state.machineMode,
      connectionState: state.connectionState,
    })),
  );

// ==========================================================================
// Helpers
// ==========================================================================

function arraysEqual(a: readonly number[], b: readonly number[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
